import React from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  Paper,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BOOKING_THEME } from '../bookingTheme';
import TimeSlotPicker from './TimeSlotPicker';
import type { AppointmentTaker } from '../../marketplace/types';
import type { TimeSlot } from '../services/appointmentService';
import { getMinTimeForToday, isToday, type AppointmentType } from '../hooks/useBookingFlow';
import {
  getDayOfWeekForCalendarDate,
  isCalendarDateBeforeToday,
} from '../../../utils/timezone';

interface StepChooseTimeProps {
  taker: AppointmentTaker;
  appointmentType: AppointmentType;
  date: Date;
  selectedSlot: TimeSlot | null;
  onTypeChange: (type: AppointmentType) => void;
  onDateChange: (date: Date) => void;
  onSlotSelect: (slot: TimeSlot) => void;
}

const StepChooseTime: React.FC<StepChooseTimeProps> = ({
  taker,
  appointmentType,
  date,
  selectedSlot,
  onTypeChange,
  onDateChange,
  onSlotSelect,
}) => {
  const tz = taker.timeZone || 'America/Toronto';
  const todayInTakerTz = isToday(date, tz);
  const allowWalkIn = taker.allowWalkIn !== false;
  const allowCall = taker.allowCall !== false;

  const shouldDisableDate = (d: Date) => {
    if (isCalendarDateBeforeToday(d, tz)) return true;
    const day = getDayOfWeekForCalendarDate(d, tz);
    if (day === 0 && !taker.allowSunday) return true;
    if (day === 6 && !taker.allowSaturday) return true;
    return false;
  };

  const emptySlotMsg = todayInTakerTz
    ? 'No slots left for today. Please choose another date.'
    : 'No available slots for this date — try another date.';

  if (!allowWalkIn && !allowCall) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        This consultant is not currently accepting bookings. Please check back later.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: BOOKING_THEME.textPrimary }}>
        Step 1: Choose Date and Time
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel
          component="legend"
          sx={{
            fontWeight: 600,
            color: BOOKING_THEME.textPrimary,
            mb: 1,
            '&.Mui-focused': { color: BOOKING_THEME.textPrimary },
          }}
        >
          Appointment Type
        </FormLabel>
        <RadioGroup
          row
          value={appointmentType}
          onChange={(e) => onTypeChange(e.target.value as AppointmentType)}
        >
          {allowWalkIn && (
            <FormControlLabel value="walk-in" control={<Radio />} label="Walk-in" />
          )}
          {allowCall && (
            <FormControlLabel value="call" control={<Radio />} label="Book a call" />
          )}
        </RadioGroup>
      </FormControl>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Select Date"
          value={date}
          onChange={(val) => val && onDateChange(val)}
          shouldDisableDate={shouldDisableDate}
          disablePast
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              sx: {
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': { borderColor: BOOKING_THEME.border },
                },
              },
            },
          }}
        />
      </LocalizationProvider>

      {date && (
        <TimeSlotPicker
          date={date}
          taker={taker}
          onSlotSelect={onSlotSelect}
          selectedSlot={selectedSlot}
          minTime={todayInTakerTz ? getMinTimeForToday(tz) : undefined}
          emptyMessage={emptySlotMsg}
        />
      )}

      {appointmentType === 'walk-in' && taker.officeAddress && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: BOOKING_THEME.promoAccordionBg,
            border: `1px solid ${BOOKING_THEME.border}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Office location
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {taker.officeAddress.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {taker.officeAddress.fullAddress}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default StepChooseTime;
